import * as Sentry from '@sentry/vue'
import { Button, notification } from 'ant-design-vue'

let supportStore
let identity
let scopeVersion = 0
let errors = new WeakMap()
const activeNotifications = new Map()

export const getSupportIdentity = state =>
  JSON.stringify([state?.sessionId, state?.user?.id, state?.associate?.id, state?.currentProject?.id])

export const getSupportScope = () => scopeVersion

export const initSupportDiagnostics = store => {
  supportStore = store
  identity = getSupportIdentity(store.state.applicationState)
  store.subscribe(() => {
    const nextIdentity = getSupportIdentity(store.state.applicationState)
    if (nextIdentity === identity) return

    identity = nextIdentity
    scopeVersion += 1
    errors = new WeakMap()
    for (const key of activeNotifications.keys()) notification.close(key)
    activeNotifications.clear()
    store.commit('agent/RESET_SUPPORT')
  })
}

export const openSupport = (eventId = null, scope = getSupportScope(), eventDescription = '') => {
  if (!supportStore?.state.applicationState.associate?.id) return

  supportStore.commit('agent/OPEN_SUPPORT', {
    eventId: scope === getSupportScope() ? eventId : null,
    eventDescription: scope === getSupportScope() ? eventDescription : ''
  })
}

// Legacy callers often show the API error again after converting it to a string.
// Only deduplicate their UI here; never resolve an event ID from the message text.
export const hasUnexpectedErrorNotification = (description, key) => {
  if (typeof description !== 'string' || activeNotifications.has(key)) return false
  return [...activeNotifications.values()].some(record => record.description === description)
}

// Keep the link on the error/request itself: identical messages are unrelated events.
export const captureUnexpectedError = (
  error,
  context,
  { source = error, description = typeof error === 'string' ? error : error?.message, scope = getSupportScope() } = {}
) => {
  // Ignore responses captured before a user/project change.
  if (scope !== getSupportScope()) return null

  const canCache = source !== null && (typeof source === 'object' || typeof source === 'function')
  const existing = canCache ? errors.get(source) : null
  let record = existing

  if (!record) {
    const eventId = Sentry.captureException(error, context)
    record = {
      eventId: Sentry.getClient() ? eventId : null,
      scope,
      notificationKey: `support-error-${eventId}`
    }
    if (canCache) errors.set(source, record)
  }

  record.description = description
  activeNotifications.set(record.notificationKey, record)
  notification.error({
    key: record.notificationKey,
    message: 'Error',
    description,
    duration: 10,
    btn: supportStore?.state.applicationState.associate?.id
      ? h =>
          h(
            Button,
            {
              props: { type: 'primary', size: 'small' },
              attrs: { 'data-testid': 'error-contact-support-button' },
              on: {
                click: () => {
                  openSupport(record.eventId, record.scope, record.description)
                  activeNotifications.delete(record.notificationKey)
                  notification.close(record.notificationKey)
                }
              }
            },
            ['Contact Support']
          )
      : undefined,
    onClose: () => activeNotifications.delete(record.notificationKey)
  })

  return record.eventId
}
